import { useEffect, useState } from 'react';
import axios from 'axios';
import { mediaFileUrl } from './mediaFileUrl';

// Company name + logo shown on every print/report header — one fetch,
// reused by every report via ReportShell rather than each view re-fetching
// it. Returns { companyName, logoUrl, ... }; logoUrl is null until the
// fetch resolves or when no logo has been uploaded (Company Settings >
// Branding). aboutUs is the Company Settings "About Us" blurb the
// pricelist reports print. instagramUrl/website/phoneDomestic/phoneIntl/
// email feed the "Keep In Touch" contact block both Pricelist reports
// print at the end of the document — blank when not set in Company
// Settings.
//
// Fetches GET /api/company-settings/report-info rather than the raw
// company-settings record and formatting phone numbers here itself — the
// phone-display rules live once, backend-side, in
// backend/services/companyReportInfo.js, which the emailed Pricelist PDF
// (pricelistPdfService.js) also calls directly. Before this split, the
// same formatting logic existed twice (once here, once in the PDF
// service) with no guarantee an edit to one would be mirrored in the
// other — a real correctness gap once it's a customer-facing document,
// not just duplicated code (issue #238's own follow-up).
//
// `initialData` (the server-rendered Pricelist PDF attachment, via
// pricelistPdfEntry.js) lets a caller supply this same shape directly
// instead of fetching it — logoUrl there is a data: URI resolved
// server-side rather than an /api/media-images/:id/file URL, since the PDF
// render has no authenticated browser session to fetch through. When
// supplied, the values seed the initial state and the fetch is skipped
// entirely (moot in a server render anyway, since effects never run
// during ReactDOMServer.renderToStaticMarkup — this guard just avoids a
// wasted network call if this hook is ever reused with initialData in a
// live browser context later).
export function useCompanyName(initialData) {
  const [companyName, setCompanyName] = useState(initialData?.companyName || '');
  const [logoUrl, setLogoUrl] = useState(initialData?.logoUrl || null);
  const [aboutUs, setAboutUs] = useState(initialData?.aboutUs || '');
  const [instagramUrl, setInstagramUrl] = useState(initialData?.instagramUrl || '');
  const [website, setWebsite] = useState(initialData?.website || '');
  const [phoneDomestic, setPhoneDomestic] = useState(initialData?.phoneDomestic || '');
  const [phoneIntl, setPhoneIntl] = useState(initialData?.phoneIntl || '');
  const [email, setEmail] = useState(initialData?.email || '');

  useEffect(() => {
    if (initialData) return;
    axios.get('/api/company-settings/report-info')
      .then((res) => {
        const data = res.data.data || {};
        setCompanyName(data.companyName || '');
        setLogoUrl(data.logo ? mediaFileUrl(data.logo.id) : null);
        setAboutUs(data.aboutUs || '');
        setInstagramUrl(data.instagramUrl || '');
        setWebsite(data.website || '');
        setPhoneDomestic(data.phoneDomestic || '');
        setPhoneIntl(data.phoneIntl || '');
        setEmail(data.email || '');
      })
      .catch(() => {
        setCompanyName('');
        setLogoUrl(null);
        setAboutUs('');
        setInstagramUrl('');
        setWebsite('');
        setPhoneDomestic('');
        setPhoneIntl('');
        setEmail('');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { companyName, logoUrl, aboutUs, instagramUrl, website, phoneDomestic, phoneIntl, email };
}
